/**
 * Runtime Configuration Loader
 *
 * Called by APP_INITIALIZER before the Angular app starts.
 * Loads /assets/config.json for staging/production and configures AWS Amplify
 * so that router guards (publicGuard) can trigger Cognito redirects.
 */

import { Amplify } from 'aws-amplify';
import { ConfigService, RuntimeConfig } from '../services/common/config.service';

export function loadRuntimeConfig(configService: ConfigService) {
  return (): Promise<void> => {
    const isLocalhost = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      console.log('🏠 Running on LOCALHOST - Cognito disabled, using existing auth flow');
      return Promise.resolve();
    }

    console.log('☁️  Loading runtime config from /assets/config.json...');

    return fetch('/assets/config.json', {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then((config: RuntimeConfig) => {
        configService.setConfig(config);
        (window as any).runtimeConfig = config;
        configureAmplifyFromConfig(config);
      })
      .catch(error => {
        console.error('❌ Failed to load runtime config:', error);
        console.warn('⚠️  Falling back to localhost config (Cognito disabled)');
      });
  };
}

export function configureAmplifyFromConfig(config: RuntimeConfig): void {
  if (!config?.cognito) {
    console.warn('⚠️  No cognito section in runtime config – Amplify not configured');
    return;
  }

  const c = config.cognito;
  const origin = window.location.origin;
  const redirectSignIn = `${origin}/auth/callback`;
  const redirectSignOut = `${origin}/account/validate-login`;

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: c.userPoolId,
        userPoolClientId: c.clientId,
        loginWith: {
          oauth: {
            domain: c.domain,
            scopes: c.scope ?? ['openid', 'email', 'profile'],
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: (c.responseType as 'code' | 'token') ?? 'code',
          },
        },
      },
    },
  });

  console.log('✅ Amplify configured – redirect:', redirectSignIn);
}

